import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import GlobalSearch from '../components/GlobalSearch';

const API = 'https://buzzinguniverse.com/backend/api';

const UserCardSkeleton = () => (
    <div className="sr-card sr-card-skeleton">
        <div className="sr-card-left">
            <div className="sr-avatar-skeleton" />
        </div>
        <div className="sr-card-body">
            <div className="sr-skel-line sr-skel-name" />
            <div className="sr-skel-line sr-skel-sub" />
            <div className="sr-skel-line sr-skel-sub short" />
        </div>
    </div>
);

const ConnectBtn = ({ user, auth, onAction }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(user.friendship_status);
    const [direction, setDirection] = useState(user.direction);

    const handleConnect = async () => {
        if (!auth?.id) return;
        setLoading(true);
        try {
            await axios.post(`${API}/send-request`, {
                sender_id: auth.id,
                receiver_id: user.id,
            });
            setStatus('pending');
            setDirection('sent');
            onAction?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (!auth?.id || auth.id === user.id) return null;

    if (status === 'friend') {
        return (
            <Link to={`/member/${auth.id}/profile`} className="sr-btn sr-btn-msg" onClick={e => e.stopPropagation()}>
                <i className="fas fa-comment-alt" /> Message
            </Link>
        );
    }
    if (status === 'pending') {
        return (
            <button className="sr-btn sr-btn-pending" disabled>
                {direction === 'sent' ? 'Pending' : 'Respond'}
            </button>
        );
    }
    return (
        <button className="sr-btn sr-btn-connect" onClick={handleConnect} disabled={loading}>
            {loading ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-user-plus" /> Connect</>}
        </button>
    );
};

const UserCard = ({ user, auth }) => {
    const photo = user.photo
        ? `https://buzzinguniverse.com/backend/${user.photo}`
        : '/images/default.png';

    const location = [user.city, user.state].filter(Boolean).join(', ');

    return (
        <div className="sr-card">
            <div className="sr-card-left">
                <Link to={`/member/${user.id}/profile`}>
                    <img src={photo} alt={user.first_name} className="sr-avatar" />
                </Link>
            </div>
            <div className="sr-card-body">
                <Link to={`/member/${user.id}/profile`} className="sr-name">
                    {user.first_name} {user.last_name}
                </Link>
                {user.about && (
                    <p className="sr-about">
                        {user.about.length > 100 ? user.about.slice(0, 100) + '…' : user.about}
                    </p>
                )}
                {location && <p className="sr-location"><i className="fas fa-map-marker-alt" /> {location}</p>}
                <div className="sr-meta">
                    {user.mutual_count > 0 && (
                        <span className="sr-mutual">
                            <i className="fas fa-user-friends" /> {user.mutual_count} mutual connection{user.mutual_count !== 1 ? 's' : ''}
                        </span>
                    )}
                    {user.friend_count > 0 && (
                        <span className="sr-friends">
                            {user.friend_count} connection{user.friend_count !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>
            <div className="sr-card-actions">
                <ConnectBtn user={user} auth={auth} />
            </div>
        </div>
    );
};

const SearchResults = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const auth = JSON.parse(secureLocalStorage.getItem('auth_data'));

    const q = searchParams.get('q') || '';
    const type = searchParams.get('type') || 'people';

    const [keyword, setKeyword] = useState(q);
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const fetchPeople = useCallback(async (kw, page, append = false) => {
        if (!kw) return;
        if (page === 1) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await axios.get(`${API}/search/people`, {
                params: { keyword: kw, user_id: auth?.id || '', page },
            });
            const data = res.data?.data;
            if (data) {
                setUsers(prev => append ? [...prev, ...data.data] : data.data);
                setCurrentPage(data.current_page);
                setLastPage(data.last_page);
                setTotal(data.total);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [auth?.id]);

    useEffect(() => {
        setKeyword(q);
        setUsers([]);
        setCurrentPage(1);
        if (q) fetchPeople(q, 1, false);
    }, [q, type]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (keyword.trim()) {
            setSearchParams({ q: keyword.trim(), type });
        }
    };

    const handleShowMore = () => {
        fetchPeople(q, currentPage + 1, true);
    };

    const setTab = (t) => setSearchParams({ q, type: t });

    return (
        <div>
            <GlobalSearch />
            <div className="sr-page">
                <div className="container">
                    <div className="sr-layout">
                        {/* Left sidebar */}
                        <aside className="sr-sidebar">
                            <div className="sr-sidebar-card">
                                <h4 className="sr-sidebar-title">Search filters</h4>
                                <ul className="sr-filter-list">
                                    <li
                                        className={type === 'people' ? 'active' : ''}
                                        onClick={() => setTab('people')}
                                    >
                                        <i className="fas fa-users" /> People
                                    </li>
                                    <li
                                        className={type === 'companies' ? 'active' : ''}
                                        onClick={() => setTab('companies')}
                                    >
                                        <i className="fas fa-building" /> Companies
                                    </li>
                                    <li
                                        className={type === 'groups' ? 'active' : ''}
                                        onClick={() => setTab('groups')}
                                    >
                                        <i className="fas fa-layer-group" /> Groups
                                    </li>
                                </ul>
                            </div>
                        </aside>

                        {/* Main content */}
                        <main className="sr-main">
                            {/* Search bar */}
                            <form className="sr-search-bar" onSubmit={handleSearch}>
                                <i className="fas fa-search sr-search-icon" />
                                <input
                                    type="text"
                                    value={keyword}
                                    onChange={e => setKeyword(e.target.value)}
                                    placeholder="Search people..."
                                    className="sr-search-input"
                                />
                                <button type="submit" className="sr-search-btn">Search</button>
                            </form>

                            {type === 'people' && (
                                <div className="sr-results-card">
                                    {q && !loading && (
                                        <p className="sr-count">
                                            {total > 0
                                                ? `${total} result${total !== 1 ? 's' : ''} for "${q}"`
                                                : `No results for "${q}"`}
                                        </p>
                                    )}

                                    {loading ? (
                                        [...Array(5)].map((_, i) => <UserCardSkeleton key={i} />)
                                    ) : (
                                        users.map(user => (
                                            <UserCard key={user.id} user={user} auth={auth} />
                                        ))
                                    )}

                                    {!loading && users.length > 0 && currentPage < lastPage && (
                                        <div className="sr-show-more">
                                            <button
                                                className="sr-btn-show-more"
                                                onClick={handleShowMore}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore
                                                    ? <><i className="fas fa-spinner fa-spin" /> Loading…</>
                                                    : 'Show more results'}
                                            </button>
                                        </div>
                                    )}

                                    {!loading && !q && (
                                        <div className="sr-empty">
                                            <i className="fas fa-search sr-empty-icon" />
                                            <p>Search for people by name</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {type !== 'people' && (
                                <div className="sr-results-card">
                                    <div className="sr-empty">
                                        <i className="fas fa-search sr-empty-icon" />
                                        <p>
                                            {type === 'companies' ? 'Company' : 'Group'} search — use the main search bar above to find {type}.
                                        </p>
                                        <Link to={`/companies`} className="sr-btn sr-btn-connect mt-3" style={{ display: 'inline-flex' }}>
                                            Browse {type === 'companies' ? 'Companies' : 'Groups'}
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchResults;
